import React from 'react';
import { isUndefined } from 'lodash-es';

import { CONFIGURATOR_DEFAULT_REGION } from '../constants';
import {
  ConfiguratorContextCountryGroup,
  ConfiguratorCountryClickHandler,
  ConfiguratorSessionState,
  ConfiguratorSessionStateRoot,
  Country,
  CountryGroup,
  OperatingRegion,
  OperatingRegionGroup,
  SetSessionCallback,
} from '../types';

// Free reverse geocoding API response structure
// {
//     "latitude": 0.00,
//     "lookupSource": "coordinates",
//     "longitude": 0.00,
//     "localityLanguageRequested": "en",
//     "continent": "Europe",
//     "continentCode": "EU",
//     "countryName": "Romania",
//     "countryCode": "RO",
//     "principalSubdivision": "Bucharest",
//     "principalSubdivisionCode": "RO-B",
//     "city": "Bucharest",
//     "locality": "Sector 6",
//     "postcode": "",
//     "plusCode": "8GP8C3R5+C3",
//     "localityInfo": {
//         "administrative": [
//             {
//                 "name": "Romania",
//                 "description": "republic in Southeast Europe",
//                 "isoName": "Romania",
//                 "order": 3,
//                 "adminLevel": 2,
//                 "isoCode": "RO",
//                 "wikidataId": "Q218",
//                 "geonameId": 798549
//             },
//             {
//                 "name": "Bucharest",
//                 "description": "capital of Romania",
//                 "order": 6,
//                 "adminLevel": 4,
//                 "wikidataId": "Q19660",
//                 "geonameId": 683506
//             },
//             {
//                 "name": "Bucharest",
//                 "description": "capital of Romania",
//                 "isoName": "Bucharest",
//                 "order": 7,
//                 "adminLevel": 4,
//                 "isoCode": "RO-B",
//                 "wikidataId": "Q19660",
//                 "geonameId": 683506
//             },
//             {
//                 "name": "Sector 6",
//                 "description": "sector of Bucharest",
//                 "order": 8,
//                 "adminLevel": 9,
//                 "wikidataId": "Q167280",
//                 "geonameId": 11048323
//             }
//         ],
//         "informative": [
//             {
//                 "name": "Europe",
//                 "description": "terrestrial continent located in north-western Eurasia",
//                 "isoName": "Europe",
//                 "order": 1,
//                 "isoCode": "EU",
//                 "wikidataId": "Q46",
//                 "geonameId": 6255148
//             },
//             {
//                 "name": "Europe/Bucharest",
//                 "description": "time zone",
//                 "order": 2
//             },
//             {
//                 "name": "Macroregiunea Trei",
//                 "description": "region in Romania",
//                 "order": 4,
//                 "wikidataId": "Q12296926"
//             },
//             {
//                 "name": "Bucuresti-Ilfov",
//                 "description": "region in Romania",
//                 "order": 5,
//                 "wikidataId": "Q3241184"
//             }
//         ]
//     }
// }

const getUserCountryCode = async (): Promise<string | undefined> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(CONFIGURATOR_DEFAULT_REGION);

      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Free reverse geocoding API
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
          );

          const data = await response.json();

          const countryCode = data.countryCode === 'MX' ? 'MEX' : CONFIGURATOR_DEFAULT_REGION;

          resolve(countryCode);
        } catch (err) {
          resolve(CONFIGURATOR_DEFAULT_REGION);
        }
      },
      () => resolve(CONFIGURATOR_DEFAULT_REGION),
    );
  });
};

export function useCountry(
  regions: OperatingRegion[] = [],
  regionGroups: OperatingRegionGroup[] = [],
  defaultCountryCode: string | undefined,
  sessionState: ConfiguratorSessionState = {},
  setSession: SetSessionCallback,
) {
  const [temporaryCountry, setTemporaryCountry] = React.useState<Country | undefined>();
  const [openSelectModal, setOpenSelectModal] = React.useState<boolean>(false);

  const countryGroups = React.useMemo<CountryGroup[]>(() => {
    return regionGroups
      .filter((regionGroup) => regionGroup.is_active)
      .map((regionGroup) => {
        const groupCountries = regions
          .filter((region) => regionGroup.maps_service_identifier.operating_region_ids.includes(region.code))
          .toSorted((a, b) => a.priority - b.priority);

        return {
          ...regionGroup,
          regions: groupCountries,
        };
      })
      .toSorted((a, b) => a.priority - b.priority);
  }, [regionGroups, regions]);

  // If we use defaultCountryCode from the configurator API
  // const country = React.useMemo<Country | undefined>(() => {
  //   const countryCode = sessionState.countryCode ?? defaultCountryCode;
  //
  //   if (!isUndefined(countryCode)) {
  //     return regions.find((region) => region.code === countryCode);
  //   }
  //
  //   return undefined;
  // }, [defaultCountryCode, regions, sessionState.countryCode]);

  // If we use browser geolocation with external service
  const country = React.useMemo<Country | undefined>(() => {
    const countryCode = temporaryCountry?.code ?? sessionState.countryCode ?? CONFIGURATOR_DEFAULT_REGION;

    if (!isUndefined(countryCode)) {
      return regions.find((region) => region.code === countryCode);
    }

    return undefined;
  }, [temporaryCountry, regions, sessionState.countryCode]);

  // If we use defaultCountryCode from the configurator API
  // React.useEffect(() => {
  //   if (isUndefined(sessionState.countryCode) && !isUndefined(defaultCountryCode)) {
  //     const rootInitialSession: ConfiguratorSessionStateRoot = {
  //       countryCode: defaultCountryCode,
  //     };
  //
  //     setSession(rootInitialSession);
  //   }
  // }, [defaultCountryCode, sessionState.countryCode, setSession]);

  // If we use browser geolocation with external service
  React.useEffect(() => {
    if (isUndefined(sessionState.countryCode)) {
      getUserCountryCode().then((countryCode) => {
        const rootInitialSession: ConfiguratorSessionStateRoot = {
          countryCode,
        };

        setSession(rootInitialSession);
      });
    }
  }, [sessionState.countryCode, setSession]);

  const handleSelectModalToggle = React.useCallback(() => {
    if (openSelectModal) {
      setTemporaryCountry(undefined);
      setOpenSelectModal(false);
    } else {
      setTemporaryCountry(country);
      setOpenSelectModal(true);
    }
  }, [openSelectModal, country]);

  const handleCountryChange = React.useCallback<ConfiguratorCountryClickHandler>((nextCountry) => {
    setTemporaryCountry(nextCountry);
  }, []);

  const handleConfirmCountry = React.useCallback(() => {
    if (isUndefined(temporaryCountry)) {
      return;
    }

    const rootInitialSession: ConfiguratorSessionStateRoot = {
      countryCode: temporaryCountry.code,
    };

    setSession(rootInitialSession);

    setTemporaryCountry(undefined);
    setOpenSelectModal(false);
  }, [setSession, temporaryCountry]);

  const contextCountries = React.useMemo<ConfiguratorContextCountryGroup[]>(() => {
    return countryGroups.map((countryGroup) => ({
      ...countryGroup,
      regions: countryGroup.regions.map((region) => ({
        ...region,
        active: region.code === country?.code,
        onClick: () => handleCountryChange(region),
      })),
    }));
  }, [countryGroups, country, handleCountryChange]);

  return React.useMemo(
    () => ({
      country,
      countries: contextCountries,
      countryOpenSelectModal: openSelectModal,
      onCountrySelectModalToggle: handleSelectModalToggle,
      onConfirmCountry: handleConfirmCountry,
    }),
    [country, contextCountries, openSelectModal, handleSelectModalToggle, handleConfirmCountry],
  );
}
